import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, SITE } from "@/lib/site";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      <header
        data-testid="site-navbar"
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color] duration-500 ${
          scrolled
            ? "bg-background/85 backdrop-blur-xl border-b border-border/60"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="max-w-[1700px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
            <img src="/assets/logo-mark.png" alt="Hive Interiors logo" className="h-9 w-auto transition-transform duration-500 group-hover:scale-105" />
            <span className="font-body text-sm font-bold tracking-[0.22em] uppercase whitespace-nowrap">
              Hive <span className="text-primary">Interiors</span>
            </span>
          </Link>

          <nav className="hidden 2xl:flex items-center gap-5" data-testid="nav-links">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.href}
                to={l.href}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-[11px] uppercase tracking-[0.18em] font-semibold whitespace-nowrap transition-colors duration-300 hover:text-primary ${
                    isActive ? "text-primary" : "text-foreground/80"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/contact"
              data-testid="nav-consultation-button"
              className="hidden md:inline-flex items-center whitespace-nowrap bg-primary text-primary-foreground px-5 py-2.5 text-[11px] uppercase tracking-[0.18em] font-bold transition-colors duration-300 hover:bg-accent hover:text-accent-foreground"
            >
              Get a Free Consultation
            </Link>
            <button
              data-testid="nav-mobile-menu-button"
              onClick={() => setOpen(true)}
              className="2xl:hidden p-2 text-foreground"
              aria-label="Open menu"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-[60] bg-background flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
          >
            <div className="flex items-center justify-between px-6 h-20 border-b border-border">
              <img src="/assets/logo-mark.png" alt="Hive Interiors logo" className="h-9 w-auto" />
              <button
                data-testid="mobile-menu-close-button"
                onClick={() => setOpen(false)}
                className="p-2"
                aria-label="Close menu"
              >
                <X className="w-7 h-7" />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-1">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.href}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.05, duration: 0.5 }}
                >
                  <NavLink
                    to={l.href}
                    data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) =>
                      `block py-2.5 font-heading text-4xl tracking-tight transition-colors duration-300 ${
                        isActive ? "text-primary italic" : "text-foreground hover:text-primary"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <div className="px-8 pb-10">
              <Link
                to="/contact"
                data-testid="mobile-nav-consultation-button"
                className="block text-center bg-primary text-primary-foreground py-4 text-xs uppercase tracking-[0.2em] font-bold"
              >
                Get a Free Consultation
              </Link>
              <p className="mt-6 text-sm text-muted-foreground">{SITE.phone} · {SITE.email}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
